import { Component } from '@angular/core';
import { filter, catchError } from 'rxjs/operators';
import { Subscription, of } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { ILink } from 'src/app/interfaces/ilink';
import { UserService } from 'src/app/services/general/user.service';
import {
  getLocalStorageRoleNames,
  isTeamManager,
  isTeamManagerInAnyAccount,
  hasPerAccountTeamRelationships,
  SelfServeLocalStorageUtil,
} from 'src/app/utils/local-storage.util';
import { DeviceService } from 'src/app/services/general/device.service';
import { NavigationEnd, Router } from '@angular/router';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslocoService } from '@jsverse/transloco';
import { SplitIoService } from 'src/app/services/external-tools/split-io.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AccountFeatureService } from 'src/app/services/account-feature/account-feature.service';
import { PersonalizedPracticeService } from 'src/app/services/personalized-practice/personalized-practice.service';
import { AppConstants } from 'src/app/app-constants';
import { NativeCallService } from 'src/app/services/native-call.service';
@Component({
  selector: 'app-layout-general',
  standalone: false,
  templateUrl: './layout-general.component.html',
  styleUrls: ['./layout-general.component.scss'],
})
export class LayoutGeneralComponent {
  links: ILink[] = [];
  isQcAdmin: boolean = false;
  isAccountAdmin: boolean = false;
  hasLearnerRole: boolean = false;
  displayIpadBanner: boolean = false;
  hasSimulationBuilderAccess: boolean = false;
  hasLeaderboardAccess: boolean = false;
  hasPersonalizedPracticeAccess: boolean = false;
  private userRolesSubscription!: Subscription;

  constructor(
    private userService: UserService,
    private deviceService: DeviceService,
    private router: Router,
    private translationService: TranslationService,
    private translocoService: TranslocoService,
    private splitIoService: SplitIoService,
    private featureFlagsService: FeatureFlagsService,
    private accountFeatureService: AccountFeatureService,
    private personalizedPracticeService: PersonalizedPracticeService,
    private nativeCallService: NativeCallService
  ) {}

  async ngOnInit() {
    await this.translationService.loadScopedTranslation('general');
    await this.translationService.loadScopedTranslation('simulationBuilder');
    // Subscribing to userRoles Observable
    this.userRolesSubscription = this.userService.userRoles$.subscribe(
      (roles) => {
        this.getCurrentRole();
      }
    );
    this.checkForIpadDevice();
    // Subscribe to router events to check for iPad device
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe(() => {
        this.checkForIpadDevice();
      });
  }

  ngOnDestroy() {
    if (this.userRolesSubscription) {
      this.userRolesSubscription.unsubscribe();
    }
  }

  get isNativeIos(): boolean {
    return this.nativeCallService.isNativeIos;
  }

  get showIosBottomNav(): boolean {
    const url = this.router.url;
    const hiddenRoutes = ['/call', '/results_conversational', '/results_guided', '/results_visual_aid'];
    return !hiddenRoutes.some((route) => url.startsWith(route));
  }

  async getCurrentRole() {
    let rolesNames: string[] = getLocalStorageRoleNames();
    this.isQcAdmin = rolesNames.includes('QCAdmin');
    this.isAccountAdmin = rolesNames.includes('AccountAdmin');
    this.hasLearnerRole = rolesNames.includes('Learner');
    this.hasSimulationBuilderAccess =
      SelfServeLocalStorageUtil.hasSimulationBuilderAccess();

    this.hasLeaderboardAccess = await this.accountFeatureService.isFeatureActive(AppConstants.ACCOUNT_FEATURE_LEADERBOARD_TAB_VISIBILITY);

    const featureFlagEnabled = await this.featureFlagsService.isFlagEnabled(
      AppConstants.PERSONALISED_PRACTICE_UI
    );
    const hasProducts = await firstValueFrom(
      this.personalizedPracticeService.hasPersonalizedPracticeProducts$
    );

    // Personalized Practice access requires both feature flag AND products availability
    this.hasPersonalizedPracticeAccess = featureFlagEnabled && hasProducts;

    const existingRelationships = localStorage.getItem('relationshipsInTeams');
    // Also refetch values cached in the legacy single-account shape, so they become per-account.
    if (!existingRelationships || !hasPerAccountTeamRelationships()) {
      this.userService
        .getTeamRelationshipsForCurrentUser()
        .pipe(catchError(() => of(null)))
        .subscribe(async (result) => {
          // On error, keep a previously cached value rather than wiping it.
          if (result !== null || !existingRelationships) {
            localStorage.setItem('relationshipsInTeams', JSON.stringify(result ?? []));
          }
          this.links = await this.getLinksWithFeatureFlag(rolesNames);
        });
    } else {
      this.links = await this.getLinksWithFeatureFlag(rolesNames);
    }
    console.log('links', this.links);
  }

  async getLinksWithFeatureFlag(rolesNames: string[]): Promise<ILink[]> {
    let links = this.getLinks(rolesNames);

    const adminNavEnabled = await this.featureFlagsService.isFlagEnabled(AppConstants.ADMIN_NAV);
    if (!adminNavEnabled) {
      links = links.filter(
        (link) => !link.route.includes('/admin?')
      );
    }

    // Check feature flag and filter dashboard link if needed
    const treatment = await this.splitIoService.getTreatment(
      AppConstants.NEW_REPORTING_UI
    );
    const myTeams = await this.splitIoService.getTreatment(
      AppConstants.MY_TEAMS_UI
    );
    const newReportingtUI = treatment === 'on';
    const myTeamsUI = myTeams === 'on';

    if (!newReportingtUI) {
      return links.filter((link) => link.route !== '/reporting_dashboard');
    }
    if (!myTeamsUI) {
      return links.filter((link) => link.route !== '/team_manager/my_teams');
    }

    // Check the account-level Leaderboard Tab Visibility feature and filter the leaderboard link if needed
    const leaderboardUI = await this.accountFeatureService.isFeatureActive(AppConstants.ACCOUNT_FEATURE_LEADERBOARD_TAB_VISIBILITY);

    if (!leaderboardUI) {
      links = links.filter((link) => link.route !== '/leaderboard');
    }

    if (this.nativeCallService.isNativeIos) {
      // Native iOS uses bottom nav — top navbar is hidden, no links needed
      return [];
    }

    return links;
  }

  getLinks(storedRoleNames: string[]): ILink[] {
    // Local copy so the derived roles below never leak into the caller's array.
    const rolesNames = [...storedRoleNames];

    // Adding TeamManager constraint as well to keep things consistent on frontend.
    if (isTeamManager()) {
      rolesNames.push('TeamManager');
    }

    // Managers of teams in a secondary account also get the Dashboard tab.
    if (isTeamManagerInAnyAccount()) {
      rolesNames.push('AnyAccountTeamManager');
    }

    const hasLearnerRole = rolesNames.includes('Learner');
    const hasTeamManagerRole = rolesNames.includes('TeamManager');
    const hasEnablementAdminRole = rolesNames.includes('EnablementAdmin');

    type Role =
      | 'TeamManager'
      | 'AnyAccountTeamManager'
      | 'Learner'
      | 'EnablementAdmin'
      | 'Author'
      | 'AssignmentManager';

    const simulationBuilderLink: ILink = {
      route: '/self-serve',
      text: this.translocoService.translate(
        'SIMULATION_BUILDER_HEADING',
        {},
        'simulationBuilder'
      ),
      icon: 'pencil-edit',
      childRoutes: [],
    };

    const adminLink: ILink = {
      route: this.userService.getAdminPortalUrl().replace('/?', '/admin?'),
      text: this.translocoService.translate('ADMIN'),
      icon: 'team-group',
      childRoutes: [],
    };

    const leaderboardLink: ILink = {
      route: '/leaderboard',
      text: this.translocoService.translate('LEADERBOARD', {}, 'general'),
      icon: 'award',
      childRoutes: [],
    };

    const personalizedPracticeLink: ILink = {
      route: '/personalized-practice',
      text: this.translocoService.translate(
        'PERSONALIZED_PRACTICE',
        {},
        'general'
      ),
      icon: 'grid',
      childRoutes: [],
    };

    const roleLinks: Partial<Record<Role, ILink[]>> = {
      TeamManager: [
        {
          route: '/team_manager/my_teams',
          text: this.translocoService.translate('MY_TEAMS', {}, 'general'),
          icon: 'users',
          childRoutes: [],
        },
        {
          route: '/reporting_dashboard',
          text: this.translocoService.translate('DASHBOARD', {}, 'general'),
          icon: 'users',
          childRoutes: [],
        },
      ],
      AnyAccountTeamManager: [
        {
          route: '/reporting_dashboard',
          text: this.translocoService.translate('DASHBOARD', {}, 'general'),
          icon: 'users',
          childRoutes: [],
        },
      ],
      Learner: [
        {
          route: '/assignments',
          text: `${this.translocoService.translate(
            'ASSIGNMENTS',
            {},
            'general'
          )}`,
          icon: 'grid',
          childRoutes: [
            '/assignment',
            '/results_conversational',
            '/results_guided',
            '/results_visual_aid',
            '/learning-pathway',
          ],
        },
      ],
      EnablementAdmin: [
        {
          route: '/reporting_dashboard',
          text: this.translocoService.translate('DASHBOARD', {}, 'general'),
          icon: 'users',
          childRoutes: [],
        },
      ],
    };

    const orderedRoles: Role[] = [
      'TeamManager',
      'AnyAccountTeamManager',
      'Learner',
      'EnablementAdmin',
      'Author',
      'AssignmentManager',
    ];

    let links: ILink[] = [];

    orderedRoles.forEach((role) => {
      if (rolesNames.includes(role) && roleLinks[role]) {
        roleLinks[role]?.forEach((link) => {
          if (
            !links.some((existingLink) => existingLink.route === link.route)
          ) {
            links.push(link);
          }
        });

        if (role === 'Learner' && this.hasPersonalizedPracticeAccess) {
          links.push(personalizedPracticeLink);
        }
      }
    });

    if (this.hasSimulationBuilderAccess) {
      links.push(simulationBuilderLink);
    }

    if (this.isQcAdmin || this.isAccountAdmin) {
      links.push(adminLink);
    }

    if (
      this.hasLeaderboardAccess &&
      (hasLearnerRole || hasTeamManagerRole || hasEnablementAdminRole)
    ) {
      links.push(leaderboardLink);
    }

    return links;
  }

  checkForIpadDevice(): void {
    // SAFE: browser UA string, not user/localized text
    const ua: string = navigator.userAgent.toLowerCase();

    // Check for iPad in the user agent string (covers older iOS devices)
    const isIpadOld: boolean = ua.includes('ipad');

    // Check for iPad based on the new iOS/macOS-like user agent string but with touch support
    const isIpadNew: boolean = /macintosh/.test(ua) && 'ontouchend' in document;

    // Return true if either condition is met
    const isIpad = isIpadNew || isIpadOld;
    this.displayIpadBanner = isIpad && this.shouldDisplayIpadBanner();
    this.deviceService.setIpadStatus(this.displayIpadBanner);
  }

  shouldDisplayIpadBanner(): boolean {
    const currentRoute = this.router.url;
    if (currentRoute.includes('/call')) {
      // Hide the banner if the current route is '/call'
      return false;
    }
    return true;
  }
}
